import { baseApi } from '@krp/client-runtime'

import { peekFrame } from '../../search/api/frames'
import type { TwinFamilies, TwinFrameAdded, TwinPriority, TwinRun, TwinRunDetail, TwinScope } from '../model/twinTypes'
import { decodeTwinFamilies, decodeTwinFrameAdded, decodeTwinRun, decodeTwinRunDetail, decodeTwinRuns } from './twinContracts'
import { PLATFORM_TESTING_TAG } from './testingApi'
import { PLATFORM_TWIN_TESTING_URLS } from './urls'

/**
 * Запросы экрана «Похожие варианты» (RP-FB67). Команды не повторяются transport-ом (небезопасный метод): неизвестный исход — решение человека. Фото уходит
 * multipart; файл берётся из реестра кадров по `frameId`, в Redux попадает только ключ.
 */
const FAMILIES = { type: PLATFORM_TESTING_TAG, id: 'TWIN_FAMILIES' } as const
const PRODUCTS = { type: PLATFORM_TESTING_TAG, id: 'PRODUCTS' } as const
const RUNS = { type: PLATFORM_TESTING_TAG, id: 'TWIN_RUNS' } as const
const run = (id: string) => ({ type: PLATFORM_TESTING_TAG, id: `twin-run:${id}` }) as const

export interface TwinFamiliesArgs {
  /** Отбор по приоритету; `null` — все семейства. */
  priority: TwinPriority | null
  take: number
}

export interface AddTwinFrameArgs {
  catalogProductId: string
  series: string
  frameId: string
}

export const twinApi = baseApi.enhanceEndpoints({ addTagTypes: [PLATFORM_TESTING_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    platformTwinFamilies: build.query<TwinFamilies, TwinFamiliesArgs>({
      query: ({ priority, take }) => ({
        url: PLATFORM_TWIN_TESTING_URLS.families,
        params: priority === null ? { take: String(take) } : { take: String(take), priority },
        decode: decodeTwinFamilies,
      }),
      providesTags: [FAMILIES],
    }),
    /** Пересчёт по каталогу — команда: сервер считает заново, а не отдаёт кэш. Ответ сразу кладётся в кэш списка. */
    platformTwinRecompute: build.mutation<TwinFamilies, TwinFamiliesArgs>({
      query: ({ priority, take }) => ({
        url: PLATFORM_TWIN_TESTING_URLS.refreshFamilies,
        method: 'POST',
        params: priority === null ? { take: String(take) } : { take: String(take), priority },
        decode: decodeTwinFamilies,
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }): Promise<void> => {
        try {
          const { data } = await queryFulfilled
          void dispatch(twinApi.util.upsertQueryData('platformTwinFamilies', args, data))
        } catch {
          // Отказ показывает результат самой команды; кэш списка остаётся прежним.
        }
      },
    }),
    platformTwinAddFrame: build.mutation<TwinFrameAdded, AddTwinFrameArgs>({
      query: ({ catalogProductId, series, frameId }) => {
        const frame = peekFrame(frameId)
        if (frame === undefined) throw new Error('Фото уже снято с отправки: выберите его ещё раз')
        const form = new FormData()
        form.append('photo', frame, frame instanceof File ? frame.name : 'photo')
        form.append('catalogProductId', catalogProductId)
        form.append('series', series)
        return { url: PLATFORM_TWIN_TESTING_URLS.frames, method: 'POST', formData: form, decode: decodeTwinFrameAdded }
      },
      // Новый тестовый товар и кадр меняют и семейства (сколько кадров у варианта), и общий список тестовых товаров.
      invalidatesTags: [FAMILIES, PRODUCTS],
    }),
    platformTwinRuns: build.query<TwinRun[], void>({
      query: () => ({ url: PLATFORM_TWIN_TESTING_URLS.runs, params: { take: '50' }, decode: decodeTwinRuns }),
      providesTags: [RUNS],
    }),
    platformTwinRun: build.query<TwinRunDetail, { runId: string }>({
      query: ({ runId }) => ({ url: PLATFORM_TWIN_TESTING_URLS.run(runId), decode: decodeTwinRunDetail }),
      providesTags: (_result, _error, { runId }) => [run(runId)],
    }),
    platformTwinStartRun: build.mutation<TwinRun, { scope: TwinScope }>({
      query: (body) => ({ url: PLATFORM_TWIN_TESTING_URLS.runs, method: 'POST', body, decode: decodeTwinRun }),
      invalidatesTags: [RUNS],
    }),
    platformTwinCancelRun: build.mutation<TwinRun, { runId: string }>({
      query: ({ runId }) => ({ url: PLATFORM_TWIN_TESTING_URLS.cancelRun(runId), method: 'POST', decode: decodeTwinRun }),
      invalidatesTags: (_result, _error, { runId }) => [RUNS, run(runId)],
    }),
  }),
})

export const {
  platformTwinFamilies,
  platformTwinRecompute,
  platformTwinAddFrame,
  platformTwinRuns,
  platformTwinRun,
  platformTwinStartRun,
  platformTwinCancelRun,
} = twinApi.endpoints
